import React from 'react';

export function useGetLatest<T>(obj: T): () => T {
    const ref = React.useRef<T>(obj);
    ref.current = obj;

    return React.useCallback(() => ref.current, []);
}

export const safeUseLayoutEffect =
    typeof document !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export function useMountedLayoutEffect(
    fn: () => void,
    deps: React.DependencyList,
): void {
    const mountedRef = React.useRef(false);

    safeUseLayoutEffect(() => {
        if (mountedRef.current) {
            fn();
        }
        mountedRef.current = true;
        // eslint-disable-next-line
    }, deps);
}

type DebounceRef = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    promise?: Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolve?: (value: any) => void;
    reject?: (reason?: unknown) => void;
    timeout?: ReturnType<typeof setTimeout>;
};

export default function useAsyncDebounce<TArgs extends unknown[]>(
    defaultFn: (...args: TArgs) => unknown,
    defaultWait: number = 0,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): (...args: TArgs) => Promise<any> {
    const debounceRef = React.useRef<DebounceRef>({});

    const getDefaultFn = useGetLatest(defaultFn);
    const getDefaultWait = useGetLatest(defaultWait);

    return React.useCallback(
        async (...args: TArgs) => {
            if (!debounceRef.current.promise) {
                debounceRef.current.promise = new Promise((resolve, reject) => {
                    debounceRef.current.resolve = resolve;
                    debounceRef.current.reject = reject;
                });
            }

            if (debounceRef.current.timeout) {
                clearTimeout(debounceRef.current.timeout);
            }

            debounceRef.current.timeout = setTimeout(async () => {
                delete debounceRef.current.timeout;
                try {
                    debounceRef.current.resolve!(await getDefaultFn()(...args));
                } catch (err) {
                    debounceRef.current.reject!(err);
                } finally {
                    delete debounceRef.current.promise;
                }
            }, getDefaultWait());

            return debounceRef.current.promise;
        },
        [getDefaultFn, getDefaultWait],
    );
}

export function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}
